import React, { useEffect, useMemo } from 'react';
import { Table, Tag, Button, theme } from 'antd';
import {
  RightOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  SendOutlined,
  ExclamationCircleOutlined,
  WarningOutlined,
  DollarOutlined,
  AlertOutlined,
  DashboardOutlined,
  FileTextOutlined,
  UserOutlined,
} from '@ant-design/icons';
import useAuth from 'hooks/useAuth';
import dayjs from 'libs/dayjs';
import { Link } from 'react-router-dom';
import paths from 'assets/routes/paths';
import SolicitudesTable from 'components/tables/SolicitudesTable';
import SolicitudesForDayTable from 'components/tables/SolicitudesForDayTable';
import CalendarWidget from 'components/calendar/CalendarWidget';
import { CardHeader, IconBadge } from 'components/headings';
import { useDashboard } from 'providers/DashboardProvider';
import { usePanelOperacional } from 'providers/PanelOperacionalProvider';
import { useCicloTablero } from 'hooks/useApi/useCicloApi';
import BandejaCiclo from 'components/ciclo/BandejaCiclo';
import { NovedadesInicio, useAyuda } from 'components/ayuda';

const { useToken } = theme;

const fmtCLP = (n) =>
  new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    maximumFractionDigits: 0,
  }).format(n || 0);

// ── KPI card ──────────────────────────────────────────────────────────
function KpiCard({ icon, tint, title, description, value, detail, detailTint }) {
  const { token } = useToken();
  return (
    <div
      style={{
        display: 'flex',
        gap: 12,
        padding: token.paddingMD,
        border: `1px solid ${token.colorBorder}`,
        borderRadius: token.borderRadiusLG,
        background: token.colorBgContainer,
        boxShadow: token.boxShadowSecondary,
      }}
    >
      <IconBadge icon={icon} tint={tint} size="md" />
      <div style={{ minWidth: 0, flex: 1 }}>
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            color: token.colorTextTertiary,
            marginBottom: 2,
          }}
        >
          {title}
        </div>
        <div
          style={{
            fontSize: 24,
            fontWeight: 700,
            lineHeight: 1.1,
            color: token.colorText,
          }}
        >
          {value}
        </div>
        <div
          style={{
            marginTop: 2,
            fontSize: 12,
            color: token.colorTextSecondary,
            lineHeight: 1.35,
          }}
        >
          {description}
        </div>
        {detail && (
          <div
            style={{
              marginTop: 4,
              fontSize: 11,
              fontWeight: 500,
              color: detailTint ?? token.colorTextSecondary,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {detail}
          </div>
        )}
      </div>
    </div>
  );
}

// Welcome hero (intencionalmente bespoke — mantenido con Tailwind)
function WelcomeHero({ user, dayOfWeek }) {
  const { abrir, novedades } = useAyuda();
  return (
    <div className="flex items-center justify-between rounded-2xl bg-gradient-to-r from-slate-800 to-slate-700 px-6 py-5 shadow-md sm:px-8">
      <div>
        <h1 className="flex items-center gap-2 text-xl font-bold text-white sm:text-2xl">
          <UserOutlined className="text-slate-400" />
          Hola, {user?.fullname}
        </h1>
        <p className="mt-1 text-sm text-slate-400">
          {dayjs().format('dddd D [de] MMMM, YYYY')} — ten un excelente {dayOfWeek}
          {novedades.sinLeer > 0 ? (
            <>
              {' · '}
              <button
                type="button"
                onClick={() => abrir({ vista: 'novedades' })}
                className="bg-transparent p-0 font-semibold text-sky-300 underline-offset-2 hover:underline"
              >
                {novedades.sinLeer === 1
                  ? '1 novedad nueva'
                  : `${novedades.sinLeer} novedades nuevas`}
              </button>
            </>
          ) : null}
        </p>
      </div>
      <Link to={paths.admin.panelOperacional}>
        <Button type="primary" size="large" icon={<DashboardOutlined />} className="!rounded-xl">
          <span className="hidden sm:inline">Panel Operacional</span>
        </Button>
      </Link>
    </div>
  );
}

function Home() {
  const { user } = useAuth();
  const { token } = useToken();
  const dayOfWeek = dayjs().format('dddd');
  const mes = dayjs().format('YYYY-MM');

  const { data: dashData, hasCalled: dashCalled, call: dashCall } = useDashboard();
  const { data: panelData, isLoading: panelLoading, call: panelCall } = usePanelOperacional();
  const { columnas: cicloColumnas, loading: cicloLoading } = useCicloTablero({ limit: 1 });

  useEffect(() => {
    if (!dashCalled) dashCall();
  }, [dashCall, dashCalled]);

  useEffect(() => {
    panelCall({ queryParams: { mes } });
  }, [mes, panelCall]);

  const emergencias = panelData?.emergencias;
  const pipeline = panelData?.pipeline;
  const criticos = panelData?.hallazgos_criticos;
  const backlog = panelData?.backlog;
  const cargaActual = panelData?.carga_actual || [];
  const solicitudes = dashData?.solicitudes;

  const backlogTotal =
    (backlog?.sin_asignar || 0) +
    (backlog?.asignadas_sin_ejecutar || 0) +
    (backlog?.en_curso_vencidas || 0);

  const emergenciasDetail = (() => {
    const parts = [];
    if (emergencias?.breakdown?.detenido > 0)
      parts.push(
        `${emergencias.breakdown.detenido} detenido${emergencias.breakdown.detenido > 1 ? 's' : ''}`
      );
    if (emergencias?.breakdown?.en_observacion > 0)
      parts.push(`${emergencias.breakdown.en_observacion} en observación`);
    if (emergencias?.breakdown?.en_servicio > 0)
      parts.push(`${emergencias.breakdown.en_servicio} en servicio`);
    return parts.join(' · ') || null;
  })();

  const backlogDetail = (() => {
    const parts = [];
    if (backlog?.sin_asignar > 0) parts.push(`${backlog.sin_asignar} sin asignar`);
    if (backlog?.asignadas_sin_ejecutar > 0)
      parts.push(`${backlog.asignadas_sin_ejecutar} asignadas sin ejecutar`);
    if (backlog?.en_curso_vencidas > 0)
      parts.push(`${backlog.en_curso_vencidas} con más de 7 días`);
    return parts.join(' · ') || null;
  })();

  // Stats del strip "Solicitudes" — tintados via tokens semánticos
  const solicitudStats = useMemo(
    () => [
      {
        key: 'asignadas',
        label: 'Asignadas',
        icon: CheckCircleOutlined,
        color: token.colorPrimary,
      },
      { key: 'enCurso', label: 'En curso', icon: ClockCircleOutlined, color: token.colorWarning },
      {
        key: 'finalizadas',
        label: 'Finalizadas',
        icon: CheckCircleOutlined,
        color: token.colorSuccess,
      },
      { key: 'enviadas', label: 'Enviadas', icon: SendOutlined, color: token.colorInfo },
      {
        key: 'sinAsignar',
        label: 'Sin asignar',
        icon: ExclamationCircleOutlined,
        color: token.colorTextTertiary,
      },
    ],
    [token]
  );

  // Columnas de la tabla "Carga actual"
  const cargaColumns = useMemo(
    () => [
      { title: 'Técnico', dataIndex: 'nombre', key: 'nombre', ellipsis: true },
      {
        title: 'Pendientes',
        dataIndex: 'pendientes',
        key: 'pendientes',
        width: 90,
        align: 'center',
        render: (v) => (
          <span
            style={{
              fontWeight: 600,
              color: v > 0 ? token.colorWarning : token.colorTextTertiary,
            }}
          >
            {v}
          </span>
        ),
      },
      {
        title: 'En curso',
        dataIndex: 'en_curso',
        key: 'en_curso',
        width: 80,
        align: 'center',
        render: (v) => (
          <span
            style={{
              fontWeight: 600,
              color: v > 0 ? token.colorPrimary : token.colorTextTertiary,
            }}
          >
            {v}
          </span>
        ),
      },
      {
        title: 'Total',
        dataIndex: 'total',
        key: 'total',
        width: 70,
        align: 'center',
        render: (v) => (
          <Tag
            color={v >= 5 ? 'red' : v >= 3 ? 'orange' : 'green'}
            style={{ fontWeight: 700, fontSize: 12 }}
          >
            {v}
          </Tag>
        ),
      },
    ],
    [token]
  );

  // Estilo común del contenedor de panel
  const panelStyle = {
    border: `1px solid ${token.colorBorder}`,
    borderRadius: token.borderRadiusLG,
    overflow: 'hidden',
    background: token.colorBgContainer,
    boxShadow: token.boxShadowSecondary,
  };

  const seeAllLink = (to, label) => (
    <Link
      to={to}
      style={{
        fontSize: 12,
        fontWeight: 600,
        color: token.colorPrimary,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
      }}
    >
      {label} <RightOutlined style={{ fontSize: 10 }} />
    </Link>
  );

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: token.marginLG,
        paddingBottom: token.paddingXL,
      }}
    >
      <WelcomeHero user={user} dayOfWeek={dayOfWeek} />

      <BandejaCiclo columnas={cicloColumnas} loading={cicloLoading} />

      {/* ── KPI strip ── */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard
          icon={AlertOutlined}
          tint={token.colorError}
          title="Emergencias del mes"
          value={panelLoading ? '—' : emergencias?.mes ?? 0}
          description="Atenciones fuera del cron preventivo programado"
          detail={emergenciasDetail}
          detailTint={
            emergencias?.breakdown?.detenido > 0 ? token.colorError : token.colorTextSecondary
          }
        />
        <KpiCard
          icon={DollarOutlined}
          tint={token.colorWarning}
          title="Esperando aprobar"
          value={panelLoading ? '—' : pipeline?.esperando_aprobar?.count ?? 0}
          description="Presupuestos enviados al cliente pendientes de respuesta"
          detail={
            pipeline?.esperando_aprobar?.monto ? fmtCLP(pipeline.esperando_aprobar.monto) : null
          }
          detailTint={token.colorWarning}
        />
        <KpiCard
          icon={WarningOutlined}
          tint={token.colorError}
          title="Hallazgos críticos"
          value={panelLoading ? '—' : criticos?.total ?? 0}
          description="Problemas graves en ascensores que siguen sin resolver"
          detail={
            (criticos?.aging_30d || 0) > 0
              ? `${criticos.aging_30d} llevan más de 30 días abiertos`
              : null
          }
          detailTint={token.colorError}
        />
        <KpiCard
          icon={FileTextOutlined}
          tint={token.colorTextSecondary}
          title="Backlog solicitudes"
          value={panelLoading ? '—' : backlogTotal}
          description="Solicitudes de mantención pendientes o atrasadas"
          detail={backlogDetail}
          detailTint={
            (backlog?.en_curso_vencidas || 0) > 0 ? token.colorError : token.colorTextSecondary
          }
        />
      </div>

      {/* ── Solicitudes strip ── */}
      <div style={panelStyle}>
        <CardHeader
          title="Solicitudes de mantenimiento"
          subtitle="Resumen rápido del estado de las solicitudes del mes en curso"
          action={seeAllLink(paths.admin.mantenciones.root, 'Ver todas')}
        />
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            columnGap: 24,
            rowGap: 8,
            padding: `${token.paddingSM}px ${token.paddingMD}px`,
          }}
        >
          {solicitudStats.map((s) => {
            const Icon = s.icon;
            return (
              <div key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Icon style={{ color: s.color, fontSize: 14 }} aria-hidden="true" />
                <span style={{ fontSize: 12, color: token.colorTextSecondary }}>{s.label}</span>
                <span style={{ fontSize: 16, fontWeight: 700, color: s.color }}>
                  {solicitudes?.[s.key] ?? 0}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Main content ── */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,10fr)_minmax(0,14fr)]">
        {/* Izquierda */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: token.marginMD }}>
          {/* Carga actual por técnico */}
          <div style={panelStyle}>
            <CardHeader
              title="Carga actual por técnico"
              subtitle="Solicitudes abiertas que cada técnico tiene asignadas ahora mismo"
              action={seeAllLink(paths.admin.panelOperacional, 'Rendimiento 12m')}
            />
            <Table
              dataSource={cargaActual}
              columns={cargaColumns}
              rowKey="rut"
              pagination={false}
              size="small"
              loading={panelLoading}
              locale={{
                emptyText: (
                  <span style={{ fontSize: 12, color: token.colorTextTertiary }}>
                    Todos los técnicos están libres
                  </span>
                ),
              }}
            />
          </div>

          {/* Lista de solicitudes */}
          <div style={panelStyle}>
            <CardHeader
              title="Lista de solicitudes"
              subtitle="Solicitudes recientes con su estado y técnico asignado"
            />
            <div style={{ overflow: 'auto', padding: `${token.paddingXS}px ${token.paddingSM}px` }}>
              <SolicitudesTable />
            </div>
          </div>

          {/* Solicitudes del día */}
          <div style={panelStyle}>
            <CardHeader
              title="Solicitudes del día"
              subtitle="Las que están programadas o en curso para hoy"
            />
            <div style={{ padding: `${token.paddingXS}px ${token.paddingSM}px` }}>
              <SolicitudesForDayTable />
            </div>
          </div>
        </div>

        {/* Derecha — Calendario y novedades (llenan el hueco bajo el calendario) */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: token.marginMD }}>
          <div style={panelStyle}>
            <CardHeader
              icon={CalendarOutlined}
              tint={token.colorSuccess}
              title="Calendario de mantenciones"
              subtitle="Agenda semanal o mensual de solicitudes preventivas"
            />
            <div style={{ padding: token.paddingSM }}>
              <CalendarWidget />
            </div>
          </div>
          <NovedadesInicio panelStyle={panelStyle} />
        </div>
      </div>
    </div>
  );
}

export default Home;
